import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  DsmButton,
  DsmCheckbox,
  DsmGrid,
  DsmIcon,
} from "@dsm-dcs/design-system-react";
import { Tooltip } from "@material-ui/core";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
// eslint-disable-next-line import/no-named-as-default
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import {
  DSM_DARK_GREY,
  DSM_SOFT_BLACK,
  processAndStageStylesV2,
} from "../../../../../../_metronic/layout";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import {
  ShrimpFacilityOutputNames,
} from "../../../../models/Facility/Processing";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import {
  ShrimpBaseline,
  ShrimpProcessingOutputItem,
} from "../../../../models/Baseline/ShrimpBaseline";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import { FormType } from "../../common";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import {
  CoolingType,
  FacilityTableItem,
} from "../../../../models/Facility/FacilityTypes";

interface ShrimpOutputFormOutputComponentProps {
  fieldItemPrefix: string;
  fieldName: ShrimpFacilityOutputNames;
  formType: FormType;
  facility: FacilityTableItem | undefined;
  index: number;
  output: ShrimpProcessingOutputItem;
  removeOutput: (index: number) => void;
  isEdibleShrimp: boolean;
}

const ShrimpOutputFormOutputComponent: FC<
  ShrimpOutputFormOutputComponentProps
> = ({
  fieldItemPrefix,
  fieldName,
  formType = FormType.Add,
  facility,
  index,
  output,
  removeOutput,
  isEdibleShrimp,
}) => {
    const intl = useIntl();
    const classes = processAndStageStylesV2() as CSSClassesList;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const userProfile = UserProfilePrefs.getInstance();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const userUOM = userProfile.getUserUnitPrefs();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
    const barnOutputMassUnit = userUOM?.unitBarnOutputMass
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      unitLong(userUOM.unitBarnOutputMass)
      : "kg";

    const [, forceUpdate] = useState<object>({});

    const formContext = useFormContext<ShrimpBaseline>();

    const [disableFields, setDisableFields] = useState<boolean>(!output.outputName);

    const coolingTypesOptions = useMemo(() => {
      const allOptions = enumToOptionsArrayWithTranslatedStrings(
        CoolingType,
        intl,
        "SUSTELL.COOLING_TYPE"
      );

      const filteredOptions = allOptions.filter((option) => {
        const key = option.value.toLowerCase() as keyof typeof facility;
        return facility?.[key] && facility?.[key] > 0;
      });

      return filteredOptions;
    }, [intl, facility]);


    const outputName = formContext.getValues(`${fieldItemPrefix}.${fieldName}.${index}.outputName`);

    useEffect(() => {
      if (outputName === "" || outputName === undefined) {
        formContext.setValue(`${fieldItemPrefix}.${fieldName}.${index}.coolingType`, "");
        formContext.setValue(`${fieldItemPrefix}.${fieldName}.${index}.weight`, undefined);
        formContext.setValue(`${fieldItemPrefix}.${fieldName}.${index}.price`, undefined);
        forceUpdate({});
      }
    }, [outputName]);

    const onOutputNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e?.target?.value || "";
      setDisableFields(!newValue);
    };

    const getCoolingTypeDefaultValue = (index: number) => {
      const formValue = formContext.getValues(
        `${fieldItemPrefix}.${fieldName}.${index}.coolingType`
      ) as string;

      if (formValue) {
        return formValue;
      }

      if (outputName && coolingTypesOptions.length === 1) {
        formContext.setValue(
          `${fieldItemPrefix}.${fieldName}.${index}.coolingType`,
          coolingTypesOptions[0].value
        );
        forceUpdate({});
      }
    };

    const getIsNameFieldRequired = (fieldName: ShrimpFacilityOutputNames): boolean => {
      if (fieldName === ShrimpFacilityOutputNames.FRESH_MEAT_AND_EDIBLE_OFFAL)
        return true;

      return false;
    }

    return (
      <div style={{ position: "relative" }}>
        <DsmGrid
          className={classes.dsmGridTwoColumnWithBorder}
          style={{
            padding: "var(--dsm-spacing-px-4)",
            paddingBottom: "0px",
            grid: "auto/1fr auto",
            alignItems: "center",
            gap: "var(--dsm-spacing-px-4)",
          }}
        >
          <DsmGrid
            style={{
              display: "grid",
              grid: "auto/1fr 0.8fr 0.8fr 0.8fr",
              gap: "var(--dsm-spacing-px-4)",
              alignItems: "start",
              padding: "0px",
            }}
          >
            <input
              ref={formContext.register()}
              type="hidden"
              name={`${fieldItemPrefix}.${fieldName}.${index}.outputId`}
              value={formContext.getValues(
                `${fieldItemPrefix}.${fieldName}.${index}.outputId`
              )}
            />
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.${fieldName}.${index}.outputName`}
              label={intl.formatMessage({
                id: `BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.PROCESSING_OUTPUT_NAME`,
              })}
              disabled={formType === FormType.View}
              tooltip={intl.formatMessage({
                id: isEdibleShrimp 
                  ? "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.PROCESSING_OUTPUT_NAME.TOOLTIP" 
                  : "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.PROCESSING_OUTPUT_NAME.3_12.TOOLTIP",
              })}
              adornment=" "
              required={getIsNameFieldRequired(fieldName)}
              type="string"
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.${fieldName}.${index}.outputName`
              )}
              changeHandler={onOutputNameChange}
            />
            <ReactHookDsmSelect
              name={`${fieldItemPrefix}.${fieldName}.${index}.coolingType`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.COOLING_TYPE",
              })}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.COOLING_TYPE.TOOLTIP",
              })}
              disabled={formType === FormType.View || disableFields}
              placeholder={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.COOLING_TYPE.PLACEHOLDER",
              })}
              options={coolingTypesOptions}
              required
              defaultValue={getCoolingTypeDefaultValue(index)}
            />

            <ReactHookDsmInput
              name={`${fieldItemPrefix}.${fieldName}.${index}.weight`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.WEIGHT",
              })}
              disabled={formType === FormType.View || disableFields}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.WEIGHT.TOOLTIP",
              })}
              adornment={barnOutputMassUnit}
              required
              type="number"
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.${fieldName}.${index}.weight`
              )}
            />

            <ReactHookDsmInput
              name={`${fieldItemPrefix}.${fieldName}.${index}.price`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.PRICE",
              })}
              disabled={formType === FormType.View || disableFields}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.PRICE.TOOLTIP",
              })}
              required
              type="number"
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.${fieldName}.${index}.price`
              )}
              adornment={`${facility?.currency || ""}/${barnOutputMassUnit}`}
            />
          </DsmGrid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              padding: "0px",
              paddingRight: "var(--dsm-spacing-px-4)",
            }}
          >
            <DsmCheckbox
              style={{ marginRight: "var(--dsm-spacing-px-2)" }}
              name={`${fieldItemPrefix}.${fieldName}.${index}.calculate`}
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.${fieldName}.${index}.calculate`
              )}
              checked={formContext.getValues(
                `${fieldItemPrefix}.${fieldName}.${index}.calculate`
              )}
              disabled={
                formType === FormType.View ||
                fieldName ===
                ShrimpFacilityOutputNames.FRESH_MEAT_AND_EDIBLE_OFFAL ||
                disableFields
              }
              onDsmChange={(e) => {
                formContext.setValue(
                  `${fieldItemPrefix}.${fieldName}.${index}.calculate`,
                  e?.target?.checked
                );
              }}
            />
            <p
              style={{
                color:
                  (fieldName !==
                    ShrimpFacilityOutputNames.FRESH_MEAT_AND_EDIBLE_OFFAL &&
                    disableFields) ||
                    fieldName ===
                    ShrimpFacilityOutputNames.FRESH_MEAT_AND_EDIBLE_OFFAL
                    ? DSM_DARK_GREY
                    : DSM_SOFT_BLACK,
                marginBottom: "0px",
                marginRight: "var(--dsm-spacing-px-2)",
              }}
            >
              {intl.formatMessage(
                {
                  id: "SUSTELL.STAGES.PROCESSING.ANIMALS.SELECT_FOOTPRINT_TO_DISPLAY",
                },
                {
                  tooltip: (
                    <Tooltip
                      title={intl.formatMessage({
                        id: "SUSTELL.STAGES.PROCESSING.ANIMALS.SELECT_FOOTPRINT_TO_DISPLAY.SHRIMP.TOOLTIP",
                      })}
                    >
                      <DsmIcon
                        name="general/help-circle"
                        className={classes.inlineTooltipClass}
                        style={{
                          position: "absolute",
                          right: 0,
                          top: "calc(50% - 8px)",
                        }}
                      />
                    </Tooltip>
                  ),
                }
              )}
            </p>
          </div>
        </DsmGrid>

        {formType !== FormType.View && index !== 0 && (
          <DsmButton
            variant="text"
            style={{
              position: "absolute",
              top: "6px",
              right: "6px",
              padding: 0,
              minWidth: "unset",
            }}
            onClick={() => {
              removeOutput(index);
            }}
          >
            <DsmIcon
              name="general/x-close"
              style={{ color: "var(--dsm-color-neutral-darker" }}
            />
          </DsmButton>
        )}
      </div>
    );
  };

export default ShrimpOutputFormOutputComponent;
